import {
  Component,
  ElementRef,
  ViewChild,
  EventEmitter,
  Output,
  HostListener,
} from "@angular/core";
import { CommonService } from "../../service/common.service";

@Component({
  selector: "app-camera",
  templateUrl: "./camera.component.html",
  styleUrls: ["./camera.component.css"],
})
export class CameraComponent {
  @ViewChild("video") videoRef: ElementRef;
  @ViewChild("canvas") canvasRef: ElementRef;
  @ViewChild("previewArea") previewAreaRef: ElementRef;

  @Output() finished = new EventEmitter<string[]>();

  stream: MediaStream = null;
  photoDataUrl: string = null;
  flashActive: boolean = false;
  previewMode: boolean = false;

  photos: string[] = []; // ★ 複数画像を保存する配列
  isFinished = false; // ★ 撮影終了後のサムネイル表示フラグ
  streamStarted = false; // ★ ストリームの開始判定
  uiOff: boolean = false;
  isLandscape: boolean = false;

  // 十字中心（CSS top:40% に合わせる）
  private crosshairX = 0.5;
  private crosshairY = 0.4;

  constructor(private commonService: CommonService) {}

  // 縦=3/4、横=4/3
  get targetAspect(): number {
    return window.matchMedia("(orientation: landscape)").matches
      ? 4 / 3
      : 3 / 4;
  }

  public async startCamera() {
    await this.openStreamForCurrentOrientation();
    this.streamStarted = true;
    this.photoDataUrl = null;
  }

  @HostListener("window:resize")
  async onResize() {
    if (!this.stream) return;

    var next = window.matchMedia("(orientation: landscape)").matches;
    if (next === this.isLandscape) return;

    this.isLandscape = next;
    await this.switchAspectDuringStreaming();
  }

  private async openStreamForCurrentOrientation() {
    this.isLandscape = window.matchMedia("(orientation: landscape)").matches;

    var constraints = this.buildConstraints(this.isLandscape);
    var stream = await navigator.mediaDevices.getUserMedia(constraints);

    this.stream = stream;

    var video = this.videoRef.nativeElement;
    video.srcObject = stream;
    await video.play().catch(function () {});
  }

  private buildConstraints(landscape: boolean): MediaStreamConstraints {
    if (landscape) {
      // 横：4/3
      return {
        video: {
          width: { min: 1280, ideal: 1920, max: 2560 },
          height: { min: 960, ideal: 1440, max: 1920 },
          aspectRatio: { ideal: 4 / 3 },
          facingMode: { ideal: "environment" },
          frameRate: { ideal: 30, max: 60 },
        },
        audio: false,
      };
    }
    // 縦：3/4
    return {
      video: {
        width: { min: 960, ideal: 1440, max: 1920 },
        height: { min: 1280, ideal: 1920, max: 2560 },
        aspectRatio: { ideal: 3 / 4 },
        facingMode: { ideal: "environment" },
        frameRate: { ideal: 30, max: 60 },
      },
      audio: false,
    };
  }

  private async switchAspectDuringStreaming() {
    var track = this.stream.getVideoTracks()[0];
    var target = this.buildConstraints(this.isLandscape).video as any;

    try {
      await track.applyConstraints(target);
      return;
    } catch (e) {
      // applyConstraints が通らない端末は再取得
    }

    this.stream.getTracks().forEach((t) => t.stop());
    this.stream = null;
    await this.openStreamForCurrentOrientation();
  }

  // ★ここが肝：表示（object-fit: cover）と一致する範囲を、動的比率で保存
  async capture() {
    await this.triggerFlash();

    var video = this.videoRef && this.videoRef.nativeElement;
    var canvas = this.canvasRef && this.canvasRef.nativeElement;
    if (!video || !canvas) return;

    var ctx = canvas.getContext("2d");
    if (!ctx) return;

    var vw = video.videoWidth;
    var vh = video.videoHeight;
    if (!vw || !vh) return;

    var rect = this.previewAreaRef.nativeElement.getBoundingClientRect();
    var cw = rect.width;
    var ch = rect.height;
    if (!cw || !ch) return;

    var targetAR = this.targetAspect;

    // cover逆算
    var scale = Math.max(cw / vw, ch / vh);
    var displayedW = vw * scale;
    var displayedH = vh * scale;
    var offsetX = (displayedW - cw) / 2;
    var offsetY = (displayedH - ch) / 2;

    var sx = offsetX / scale;
    var sy = offsetY / scale;
    var sw = cw / scale;
    var sh = ch / scale;

    // 見えている範囲内で targetAR に合わせる（十字中心基準）
    var visibleAR = sw / sh;
    var fx = sx + sw * this.crosshairX; // 0.5
    var fy = sy + sh * this.crosshairY; // 0.4

    if (Math.abs(visibleAR - targetAR) > 1e-3) {
      if (visibleAR > targetAR) {
        var newSw = sh * targetAR;
        var newSx = fx - newSw / 2;
        if (newSx < sx) newSx = sx;
        if (newSx > sx + sw - newSw) newSx = sx + sw - newSw;
        sx = newSx;
        sw = newSw;
      } else {
        var newSh = sw / targetAR;
        var newSy = fy - newSh / 2;
        if (newSy < sy) newSy = sy;
        if (newSy > sy + sh - newSh) newSy = sy + sh - newSh;
        sy = newSy;
        sh = newSh;
      }
    }

    // 出力解像度（長辺揃え）
    var longSide = 1920;
    var outW, outH;
    if (targetAR >= 1) {
      outW = longSide;
      outH = Math.round(longSide / targetAR);
    } else {
      outH = longSide;
      outW = Math.round(longSide * targetAR);
    }

    canvas.width = outW;
    canvas.height = outH;

    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, outW, outH);

    this.photoDataUrl = canvas.toDataURL("image/jpeg", 0.95);
  }

  async ngAfterViewInit() {}

  onBgPointerDown(e: PointerEvent) {
    const el = e.target as HTMLElement | null;
    if (!el) return;

    // 操作系要素をタップしたら UI トグルしない（←ここが重要）
    if (
      el.closest(
        'button,a,input,textarea,select,label,[role="button"],[tabindex]'
      )
    )
      return;
    // 「UI対象(.ui)」をタップした場合もトグルしない
    if (el.closest(".ui")) return;
    this.uiOff = !this.uiOff;
  }

  startCameraPc() {
    const constraints = {
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: false,
    };

    navigator.mediaDevices
      .getUserMedia(constraints)
      .then((stream) => {
        this.stream = stream;
        const video = this.videoRef.nativeElement;
        video.srcObject = stream;
        video.play();
        this.streamStarted = true;
        this.flashActive = false;
        this.previewMode = false;
        this.photoDataUrl = null;
      })
      .catch((err) => console.error("PCカメラ取得エラー:", err));
  }

  stopCamera() {
    try {
      if (this.stream) {
        // すべてのトラック（映像/音声）を停止
        this.stream.getTracks().forEach((track) => {
          track.stop();
        });
      }

      // video から stream を外す（Safari対策）
      const video = this.videoRef.nativeElement;
      if (video) {
        video.pause();
        video.srcObject = null;
        video.removeAttribute("src");
        video.load(); // Safariで確実に解放
      }
    } catch (e) {
      console.error("stopCamera error:", e);
    } finally {
      this.stream = null;
      this.streamStarted = false;
      this.isFinished = false;
      this.photos = [];
    }
  }

  confirmationCapture(img) {
    // 変数に写真をためる
    this.photos.push(img);
    console.log(this.photos);
    this.isFinished = true; // ★ サムネイル表示モードに入る
  }

  triggerFlash(): Promise<void> {
    return new Promise((resolve) => {
      this.flashActive = true;

      // 発光時間
      setTimeout(() => {
        this.flashActive = false;
        // 完了を通知
        resolve();
      }, 150);
    });
  }

  retake(img = null) {
    if (img) {
      // 変数に写真をためる
      this.photos.push(img);
      console.log(this.photos);
    }
    this.previewMode = false;
    this.photoDataUrl = null;
    this.isFinished = false;
  }

  // プレビュー
  preview(index: number) {
    this.previewMode = true;
    this.photoDataUrl = this.photos[index]; // そのままプレビュー画面へ
    this.isFinished = false; // 撮影終了画面を閉じる
  }

  // 削除
  delete(index: number) {
    this.photos.splice(index, 1);
  }

  // サムネイルから戻る
  thumbnail() {
    this.previewMode = false;
    this.photoDataUrl = null;
    this.isFinished = true;
  }

  finishCapture() {
    // 親へ結果を返す
    this.finished.emit(this.photos);
    this.stopCamera();
  }
}



// WEBPACK FOOTER //
// ./src/app/parts/camera/camera.component.ts